import { useEffect, useRef } from "react";

import { decrementHashedTimes } from "@/hooks/useHashQueryId";
import {
  WEBVIEW_OO_SET_LOADING,
  WEBVIEW_RENDER_BACK_PRESS,
  clickBackButton,
  parseBridgeMessage,
  postToWebView,
} from "@/utils/webview";

/**
 * The other half of `useHashQueryId` — the original mounts this listener in its
 * route-loading provider inside `_app`.
 *
 * When the customer presses back, the synthetic history entry a screen change
 * pushed is popped. Rather than let that do nothing visible, the listener
 * clicks the topmost back control, so back closes one overlay screen at a time
 * and only leaves the page once the stack is empty.
 *
 * The 100ms timeout is the original's, and it is not arbitrary: `popstate`
 * fires before React has processed anything, so clicking immediately can hit a
 * control that is about to unmount. Holding the back button coalesces into a
 * single click because the timeout is cleared and restarted. Note the original
 * calls the click helper WITHOUT its debounce flag here — that flag belongs to
 * the RN shell's hardware-back path.
 *
 * Renders nothing.
 */
export default function BackButtonBridge() {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    const onPopState = () => {
      decrementHashedTimes();
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        clickBackButton();
      }, 100);
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      clearTimeout(timer.current);
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  /**
   * The RN shell's hardware-back bridge — the original's `T_`, verbatim:
   *
   * ```js
   * let r = (e) => {
   *   if (e.origin !== window.location.origin) return;
   *   let t = (0, Tm.o)(e.data);
   *   if (!t) return;
   *   let { type: r } = t;
   *   r && r === e0.WEBVIEW_RENDER_BACK_PRESS && (0, nl.yZ)(!0);
   * };
   * useEffect(() => {
   *   let e = { isLoading: !1 };
   *   return (
   *     (0, nl.HY)(e0.WEBVIEW_OO_SET_LOADING, e),
   *     window.addEventListener("message", r),
   *     () => { window.removeEventListener("message", r); }
   *   );
   * }, []);
   * ```
   *
   * This tree had NO `message` listener at all, so the shell's
   * `WEBVIEW_RENDER_BACK_PRESS` — which it does post, correctly — arrived
   * nowhere and the hardware Back button did nothing: it neither closed an
   * overlay nor left the app.
   *
   * Three details are the original's and are not incidental:
   *
   * - The **origin check comes first**. An RN web view delivers the shell's
   *   `injectJavaScript` post with the page's own origin, so this passes for
   *   the shell and rejects anything an embedded iframe or an extension sends.
   * - `clickBackButton(true)` is called **with** the debounce, unlike the
   *   `popstate` path above. A held-down hardware button repeats, and without
   *   the 300ms gate it would unwind several screens on one press.
   * - It is a **separate effect** from `popstate`, because it is a separate
   *   bridge: on plain web only `popstate` fires, inside the shell both do, and
   *   collapsing them would make one path's cleanup the other's.
   */
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const message = parseBridgeMessage(event.data);
      if (!message) return;
      const { type } = message;
      if (type && type === WEBVIEW_RENDER_BACK_PRESS) clickBackButton(true);
    };

    postToWebView(WEBVIEW_OO_SET_LOADING, { isLoading: false });
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, []);

  return null;
}
